import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
  Button,
  CircularProgress,
  Skeleton,
  Grid,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import GetAppIcon from '@mui/icons-material/GetApp';
import dayjs from 'dayjs';
import useVoucher from '../../hooks/useVoucher';
import VoucherHistory from './VoucherPaymentHistory';
import { getStatusLabel } from '../../util/paymentUtils';
import LockIconWrapper from '../components/LockIconWrapper';
import VoucherEmailSection from './VoucherEmailSection';
import { useAuth } from '../../context/authContext';
import { toast } from 'react-toastify';

function VoucherDetailsModal({ open, onClose, voucherId }) {
  const { checkRightAny, currentUser } = useAuth();
  const { getVoucherById, getVoucherDownloadURL, resendVoucherEmail, updateVoucherBuyerEmail } =
    useVoucher();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const fetchVoucher = async () => {
      if (!open || !voucherId) {
        setVoucher(null);
        setPdfLoading(false);
        return;
      }
      setLoading(true);
      setPdfLoading(false);
      try {
        const fetchedVoucher = await getVoucherById(voucherId);
        setVoucher(fetchedVoucher);
      } catch (err) {
        toast.error('Fehler beim Laden des Gutscheins');
        console.error('Error fetching voucher:', err);
      }
      setLoading(false);
    };

    fetchVoucher();
  }, [voucherId, open]);

  const getStatusColor = status => {
    switch (status) {
      case 'active':
        return 'success';
      case 'waitingForPayment':
        return 'warning';
      case 'used':
        return 'info';
      case 'locked':
        return 'error';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDownloadPdf = async () => {
    if (!voucher || voucher.status === 'waitingForPayment') {
      toast.error('Gutschein muss erst bezahlt werden, bevor er heruntergeladen werden kann.');
      return;
    }

    const toastMessage = `Die Download-Anfrage für Gutschein ${voucher.voucherCode} wurde gespeichert und kann später in den offenen Anfragen bestätigt werden`;

    const displayInfo = {
      Typ: 'Gutschein PDF Download',
      Gutscheincode: voucher.voucherCode,
      Status:
        voucher.status === 'active'
          ? 'Aktiv'
          : voucher.status === 'used'
            ? 'Eingelöst'
            : voucher.status,
      Restguthaben: `${voucher.currentBalance?.sum?.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`,
    };

    const onStored = response => {
      // When the stored request is executed and returns a response, open the PDF
      if (response && response.preSignedURL) {
        window.open(response.preSignedURL, '_blank');
      }
      // Close the modal when the request is stored due to authentication failure
      onClose();
    };

    setPdfLoading(true);
    try {
      const response = await getVoucherDownloadURL(
        voucher._id,
        toastMessage,
        onStored,
        displayInfo
      );
      if (response && response.preSignedURL) {
        window.open(response.preSignedURL, '_blank');
      }
      // If response is null, it means the request was stored due to insufficient rights
      // No need to show an error message as useHttp handles this
    } catch (err) {
      toast.error('Fehler beim Download des Gutscheins');
      console.error('Unexpected error during voucher download:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const isCreator = voucher?.meta?.createdBy?._id === currentUser?.userId;
  const canDownload = checkRightAny('voucher:special:download') || isCreator;

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          onClose();
          // Reset PDF loading state when modal closes
          setPdfLoading(false);
        }}
        aria-labelledby='voucher-details-modal'
        aria-describedby='voucher-details-description'
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            overflow: 'auto',
            borderRadius: '10px',
          }}
        >
          {loading ? (
            <Stack spacing={2}>
              <Skeleton variant='text' width='60%' height={40} />
              <Skeleton variant='rectangular' width='100%' height={100} />
              <Stack direction='row' spacing={2}>
                <Skeleton variant='rectangular' width='30%' height={200} />
                <Skeleton variant='rectangular' width='70%' height={200} />
              </Stack>
            </Stack>
          ) : !voucher ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                <Typography variant='h5'>Gutschein: {voucher.voucherCode}</Typography>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Chip
                    label={getStatusLabel(voucher.status)}
                    color={getStatusColor(voucher.status)}
                    size='small'
                  />
                  <IconButton onClick={onClose}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Stack>

              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography variant='body2'>
                        <strong>Gutscheincode:</strong> {voucher.voucherCode}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Status:</strong> {getStatusLabel(voucher.status)}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Ablaufdatum:</strong>{' '}
                        {voucher.expirationDate
                          ? dayjs(voucher.expirationDate).format('DD.MM.YYYY')
                          : '-'}
                      </Typography>
                      <VoucherEmailSection
                        email={voucher.buyerMail}
                        status={voucher.status}
                        onResend={() => resendVoucherEmail(voucher._id)}
                        onEdit={email => updateVoucherBuyerEmail(voucher._id, email)}
                        onEmailUpdated={email =>
                          setVoucher(prev => ({ ...prev, buyerMail: email }))
                        }
                      />
                      {voucher.buyerPostcode && (
                        <Typography variant='body2'>
                          <strong>Käufer PLZ:</strong> {voucher.buyerPostcode}
                        </Typography>
                      )}
                      {voucher.relatedVoucherProductId && (
                        <Typography variant='body2'>
                          <strong>Produkt:</strong> {voucher.relatedVoucherProductId.name} (
                          {voucher.relatedVoucherProductId.branchId?.longName ||
                            'Unbekannte Filiale'}
                          )
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>

                {/* Balances */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Stack spacing={1} sx={{ flex: 1 }}>
                      <Typography variant='body2'>
                        <strong>Ursprüngliches Guthaben:</strong>{' '}
                        {voucher.initialBalance?.sum?.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Aktuelles Guthaben:</strong>{' '}
                        {voucher.currentBalance?.sum?.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </Typography>
                      <Box sx={{ pl: 2, mt: 0.5 }}>
                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                          davon bezahltes Guthaben:{' '}
                          {voucher.currentBalance?.normalBalance?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          €
                        </Typography>
                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                          davon geschenktes Guthaben:{' '}
                          {voucher.currentBalance?.giftedBalance?.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          €
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                {/* Redemption Conditions */}
                {voucher.redemptionConditions &&
                  (voucher.redemptionConditions.minPurchaseAmount ||
                    voucher.redemptionConditions.maxUsagePerTransaction ||
                    voucher.redemptionConditions.usageScope === 'bookingOnly' ||
                    (voucher.redemptionConditions.restrictedBranches &&
                      voucher.redemptionConditions.restrictedBranches.length > 0) ||
                    (voucher.redemptionConditions.restrictedEntertainments &&
                      voucher.redemptionConditions.restrictedEntertainments.length > 0) ||
                    (voucher.redemptionConditions.restrictedEntertainmentTypes &&
                      voucher.redemptionConditions.restrictedEntertainmentTypes.length > 0)) && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant='h6' gutterBottom>
                          Einlösungsbedingungen
                        </Typography>
                        <Stack spacing={1}>
                          {voucher.redemptionConditions?.minPurchaseAmount && (
                            <Typography variant='body2'>
                              <strong>Mindestkaufbetrag:</strong>{' '}
                              {voucher.redemptionConditions.minPurchaseAmount.toLocaleString(
                                'de-DE',
                                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                              )}{' '}
                              €
                            </Typography>
                          )}
                          {voucher.redemptionConditions?.maxUsagePerTransaction && (
                            <Typography variant='body2'>
                              <strong>Max. Verwendung pro Transaktion:</strong>{' '}
                              {voucher.redemptionConditions.maxUsagePerTransaction.toLocaleString(
                                'de-DE',
                                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                              )}{' '}
                              €
                            </Typography>
                          )}
                          {voucher.redemptionConditions?.restrictedBranches?.length > 0 && (
                            <Typography variant='body2'>
                              <strong>Eingeschränkte Branches:</strong>{' '}
                              {voucher.redemptionConditions.restrictedBranches.length}
                            </Typography>
                          )}
                          {voucher.redemptionConditions?.restrictedEntertainments?.length > 0 && (
                            <Typography variant='body2'>
                              <strong>Eingeschränkte Entertainments:</strong>{' '}
                              {voucher.redemptionConditions.restrictedEntertainments.length}
                            </Typography>
                          )}
                          {voucher.redemptionConditions?.restrictedEntertainmentTypes?.length >
                            0 && (
                            <Typography variant='body2'>
                              <strong>Eingeschränkte Entertainment-Typen:</strong>{' '}
                              {voucher.redemptionConditions.restrictedEntertainmentTypes.join(', ')}
                            </Typography>
                          )}
                          {voucher.redemptionConditions?.usageScope === 'bookingOnly' && (
                            <Typography variant='body2'>
                              <strong>Verwendbar für:</strong> Nur die gebuchte Leistung (keine
                              Speisen, Getränke oder Extras)
                            </Typography>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                {/* Meta Information */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant='body2'>
                        <strong>Erstellt am:</strong>{' '}
                        {voucher.meta?.createdAt
                          ? dayjs(voucher.meta.createdAt).format('DD.MM.YYYY HH:mm')
                          : '-'}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Erstellt von:</strong>{' '}
                        {(voucher.meta?.createdBy?.firstName && voucher.meta?.createdBy?.lastName
                          ? `${voucher.meta.createdBy.firstName} ${voucher.meta.createdBy.lastName}`
                          : voucher.meta?.createdBy?.username) ||
                          voucher.meta?.createdByString ||
                          '-'}
                      </Typography>
                      {voucher.meta?.editedAt && (
                        <Typography variant='body2'>
                          <strong>Zuletzt bearbeitet am:</strong>{' '}
                          {dayjs(voucher.meta.editedAt).format('DD.MM.YYYY HH:mm')}
                        </Typography>
                      )}
                      {voucher.meta?.editedBy && (
                        <Typography variant='body2'>
                          <strong>Zuletzt bearbeitet von:</strong>{' '}
                          {voucher.meta.editedBy.firstName && voucher.meta.editedBy.lastName
                            ? `${voucher.meta.editedBy.firstName} ${voucher.meta.editedBy.lastName}`
                            : voucher.meta.editedBy.username}
                        </Typography>
                      )}
                      {voucher.creationComment && (
                        <Typography variant='body2'>
                          <strong>Erstellungskommentar:</strong> {voucher.creationComment}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Transaction History and Download Buttons */}
              <Box display='flex' justifyContent='center' gap={2}>
                <Button
                  variant='contained'
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryOpen(true)}
                  disabled={!voucher.transactionHistory || voucher.transactionHistory.length === 0}
                >
                  Transaktionsverlauf anzeigen
                </Button>
                <Tooltip
                  title={
                    !canDownload
                      ? 'Keine Berechtigung zum Download'
                      : voucher.status === 'waitingForPayment'
                        ? 'Gutschein muss erst bezahlt werden'
                        : 'PDF anzeigen'
                  }
                >
                  <span>
                    <Button
                      variant='outlined'
                      startIcon={pdfLoading ? <CircularProgress size={16} /> : <GetAppIcon />}
                      onClick={handleDownloadPdf}
                      disabled={
                        !canDownload || voucher.status === 'waitingForPayment' || pdfLoading
                      }
                    >
                      {isCreator ? (
                        'PDF anzeigen'
                      ) : (
                        <LockIconWrapper right='voucher:special:download'>
                          PDF anzeigen
                        </LockIconWrapper>
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <VoucherHistory
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        voucher={voucher}
        loading={false}
      />
    </>
  );
}

export default VoucherDetailsModal;
